import React, { useState, useEffect, useRef } from 'react';
import { TopNav, Footer, MonoLabel } from './ui';
import { getTestModeFeedback } from '../services/feedback';
import { CriterionCard, RepeatedVocabulary } from './FeedbackDisplay';
import { isTrial } from '../data/storage';
import { canWriteEssay, getSubscriptionStatus } from '../utils/subscription';

const TRIAL_PROMPT = {
  id: 'trial-fixed',
  title: 'Rewarding Students',
  text: 'Some people say that students who achieve the highest scores in their exams should be rewarded. Others say that those who show progress should be rewarded instead. Discuss both views and give your own opinion.',
  type: 'discuss',
  year: 'Trial Question',
};

const EXAM_MINUTES = 40;

const ERROR_COLORS = {
  grammar:     { fg:'#c0392b', bg:'rgba(192,57,43,0.10)', border:'#c0392b' },
  punctuation: { fg:'#7b3f00', bg:'rgba(123,63,0,0.10)',  border:'#7b3f00' },
  vocabulary:  { fg:'#1a5fa8', bg:'rgba(26,95,168,0.10)', border:'#1a5fa8' },
  phrasing:    { fg:'#6b21a8', bg:'rgba(107,33,168,0.10)',border:'#6b21a8' },
  structure:   { fg:'#b8620a', bg:'rgba(184,98,10,0.10)', border:'#b8620a' },
};

function Tooltip({ errorType, text, onClose }) {
  const c = ERROR_COLORS[errorType] || ERROR_COLORS.grammar;
  return (
    <span
      onClick={e => e.stopPropagation()}
      style={{
        position:'absolute', bottom:'calc(100% + 6px)', left:'50%',
        transform:'translateX(-50%)', background:'#1a2a4a', color:'#fff',
        padding:'8px 12px', borderRadius:8, fontSize:11.5, lineHeight:1.5,
        width:210, zIndex:200, boxShadow:'0 4px 20px rgba(0,0,0,0.3)',
        whiteSpace:'normal', pointerEvents:'none',
      }}
    >
      {errorType && (
        <span style={{ display:'block', fontWeight:700, fontSize:10, letterSpacing:'.08em',
          textTransform:'uppercase', color:c.fg, marginBottom:4 }}>
          {errorType}
        </span>
      )}
      {text}
      <span style={{ position:'absolute', top:'100%', left:'50%', transform:'translateX(-50%)',
        border:'5px solid transparent', borderTopColor:'#1a2a4a', display:'block', width:0, height:0 }}/>
    </span>
  );
}

const CRITERIA = [
  { key:'TA',  abbr:'TA',  label:'Task Achievement'     },
  { key:'CC',  abbr:'CC',  label:'Coherence & Cohesion' },
  { key:'LR',  abbr:'LR',  label:'Lexical Resource'     },
  { key:'GRA', abbr:'GRA', label:'Grammar'              },
];

export default function TestMode({ branding, candidate, prompt: promptProp, essayType, onBack, onUsed }) {
  const isTrialUser = isTrial(candidate);
  const prompt = isTrialUser ? TRIAL_PROMPT : promptProp;

  const canWrite  = canWriteEssay(candidate);
  const subStatus = canWrite ? null : getSubscriptionStatus(candidate);

  const [phase, setPhase]               = useState('intro');
  const [essay, setEssay]               = useState('');
  const [timeLeft, setTimeLeft]         = useState(EXAM_MINUTES * 60);
  const [results, setResults]           = useState(null);
  const [loading, setLoading]           = useState(false);
  const [error, setError]               = useState('');
  const [activeTooltip, setActiveTooltip] = useState(null);
  const [mobileView, setMobileView]     = useState('original');
  const timerRef                        = useRef(null);

  const wc = essay.split(/\s+/).filter(Boolean).length;

  useEffect(() => {
    if (phase === 'writing') {
      timerRef.current = setInterval(() => {
        setTimeLeft(t => {
          if (t <= 1) { clearInterval(timerRef.current); handleSubmit(); return 0; }
          return t - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timerRef.current);
  }, [phase]);

  function formatTime(secs) {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}`;
  }

  const timeColor = timeLeft < 300 ? '#c0392b' : timeLeft < 600 ? '#b8620a' : '#1a7a4a';
  const timeBg    = timeLeft < 300 ? '#fff5f5' : timeLeft < 600 ? '#fff8f0' : '#f0faf4';

  async function handleSubmit() {
    clearInterval(timerRef.current);
    if (essay.trim().split(/\s+/).filter(Boolean).length < 50) {
      setError('Please write at least 50 words before submitting.');
      return;
    }
    setPhase('scoring');
    setLoading(true);
    try {
      const parsed = await getTestModeFeedback({ promptText: prompt.text, essay });
      setResults(parsed);
      setPhase('results');
      onUsed && onUsed('test');
    } catch(e) {
      setError('Could not score essay: ' + e.message);
      setPhase('writing');
    }
    setLoading(false);
  }

  // Subscription gate
  if (!canWrite) return (
    <div style={{ minHeight:'100vh', background:'#f8f9fb', display:'flex', alignItems:'center', justifyContent:'center', padding:24, fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
      <div style={{ maxWidth:480, width:'100%', background:'#fff', border:'1px solid #e8edf5', borderRadius:16, padding:32, boxShadow:'0 4px 24px rgba(26,42,74,0.09)', textAlign:'center' }}>
        <div style={{ fontSize:48, marginBottom:14 }}>🔒</div>
        <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:22, fontWeight:700, color:'#1a2a4a', marginBottom:8 }}>
          {subStatus === 'expired' ? 'Subscription Expired' : 'Access Restricted'}
        </div>
        <div style={{ fontSize:13.5, color:'#5a6a88', lineHeight:1.7, marginBottom:22 }}>
          {subStatus === 'expired'
            ? 'Your subscription has expired. Contact Mr. Ahmed Salem to renew.'
            : 'Your subscription is not active. Contact Mr. Ahmed Salem to activate your access.'}
        </div>
        <a href="https://wa.me/201008182425" target="_blank" rel="noreferrer"
          style={{ display:'inline-flex', alignItems:'center', gap:8, background:'#25d366', color:'#fff', borderRadius:10, padding:'12px 22px', fontSize:14, fontWeight:700, textDecoration:'none', marginBottom:16 }}>
          Contact Mr. Ahmed Salem
        </a>
        <div><button onClick={onBack} style={{ background:'none', border:'none', color:'#9aa3b8', fontSize:12, cursor:'pointer', fontFamily:"'JetBrains Mono',monospace" }}>← Back</button></div>
      </div>
    </div>
  );

  // ── INTRO ──────────────────────────────────────────────────────
  if (phase === 'intro') return (
    <div style={{ minHeight:'100vh', background:'#f0f4f8', display:'flex', flexDirection:'column' }}>
      <TopNav branding={branding} onBack={onBack} backLabel="Back"/>
      <div style={{ maxWidth:680, margin:'40px auto', padding:'0 20px', flex:1, width:'100%' }}>
        <div style={{ background:'#ffffff', border:'1px solid #dde3ed', borderRadius:14, overflow:'hidden', boxShadow:'0 4px 20px rgba(26,42,74,0.08)' }}>
          <div style={{ background:'#1a2a4a', padding:'24px 28px', textAlign:'center' }}>
            <div style={{ fontFamily:"'JetBrains Mono',monospace", fontSize:11, letterSpacing:'.2em', textTransform:'uppercase', color:'#b8860b', marginBottom:10 }}>Test Mode</div>
            <h1 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:28, color:'#ffffff', marginBottom:6 }}>Exam Simulation</h1>
            <div style={{ fontSize:13, color:'rgba(255,255,255,0.65)' }}>Real exam conditions · {EXAM_MINUTES} minutes · AI scoring across all 4 criteria</div>
          </div>
          <div style={{ padding:'28px' }}>
            <div style={{ background:'#f7f9fc', border:'1px solid #dde3ed', borderRadius:9, padding:'16px 18px', marginBottom:20 }}>
              <MonoLabel style={{ marginBottom:6 }}>Your Question</MonoLabel>
              <div style={{ fontSize:14, color:'#1a2233', lineHeight:1.75, fontStyle:'italic' }}>{prompt.text}</div>
            </div>
            <div style={{ marginBottom:20 }}>
              <div style={{ fontSize:14, fontWeight:600, color:'#1a2233', marginBottom:12 }}>Exam Rules:</div>
              {[
                '⏱  You have exactly 40 minutes — a countdown timer will be shown',
                '✏️  Write your full essay in the text area — no formulas or hints',
                '📝  Aim for at least 250 words',
                '🤖  When you submit, Claude will score your essay across all 4 IELTS criteria',
                '🔒  You cannot pause the timer once you begin',
              ].map((r,i) => (
                <div key={i} style={{ fontSize:13, color:'#5a6a88', padding:'7px 0', borderBottom:'1px solid #f0f4f8', lineHeight:1.6 }}>{r}</div>
              ))}
            </div>
            <button onClick={()=>setPhase('writing')}
              style={{ width:'100%', background:'#b8860b', color:'#ffffff', border:'none', padding:14, borderRadius:9, fontSize:15, fontWeight:600, cursor:'pointer', boxShadow:'0 2px 8px rgba(184,134,11,0.35)', fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
              Start 40-Minute Timer →
            </button>
            <div style={{ textAlign:'center', marginTop:10, fontSize:12, color:'#9aa3b8' }}>Once you click Start, the timer begins immediately</div>
          </div>
        </div>
      </div>
      <Footer branding={branding}/>
    </div>
  );

  // ── SCORING ────────────────────────────────────────────────────
  if (phase === 'scoring') return (
    <div style={{ minHeight:'100vh', background:'#f0f4f8', display:'flex', alignItems:'center', justifyContent:'center', flexDirection:'column', gap:16 }}>
      <div style={{ width:52, height:52, border:'3px solid #b8860b', borderTopColor:'transparent', borderRadius:'50%', animation:'spin 1s linear infinite' }}/>
      <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:22, color:'#1a2a4a', fontWeight:700 }}>Scoring Your Essay…</div>
      <div style={{ fontSize:13, color:'#5a6a88' }}>Claude is evaluating your writing across all 4 IELTS criteria</div>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );

  // ── RESULTS ────────────────────────────────────────────────────
  if (phase === 'results' && results) {
    const orig = results.original_with_errors || [];
    const impr = results.improved_version     || [];

    return (
      <div style={{ minHeight:'100vh', background:'#f0f4f8', display:'flex', flexDirection:'column', fontFamily:"'Plus Jakarta Sans',sans-serif" }}
        onClick={() => setActiveTooltip(null)}>
        <style>{`
          .tm-grid { display:grid; grid-template-columns:1fr 1fr; gap:16px; align-items:start; }
          .tm-toggle { display:none; border-radius:8px; overflow:hidden; border:1px solid #dde3ed; margin-bottom:14px; }
          @media (max-width:768px) {
            .tm-grid { grid-template-columns:1fr; }
            .tm-toggle { display:flex; }
            .tm-mobile-hide { display:none !important; }
          }
        `}</style>

        <TopNav branding={branding} onBack={onBack} backLabel="Back"/>

        <div style={{ maxWidth:1120, margin:'0 auto', padding:'24px 16px', flex:1, width:'100%' }}>

          {/* ── Score header ── */}
          <div style={{ background:'#1a2a4a', borderRadius:14, padding:'24px 28px', marginBottom:16, boxShadow:'0 4px 20px rgba(26,42,74,0.15)' }}>
            <div style={{ textAlign:'center', marginBottom:16 }}>
              <div style={{ fontFamily:"'JetBrains Mono',monospace", fontSize:10, letterSpacing:'.2em', textTransform:'uppercase', color:'rgba(255,255,255,0.45)', marginBottom:6 }}>Overall Band Score</div>
              <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:72, fontWeight:700, color:'#b8860b', lineHeight:1 }}>{results.band_score}</div>
            </div>
            <div style={{ display:'flex', gap:10, justifyContent:'center', flexWrap:'wrap' }}>
              {CRITERIA.map(c => (
                <div key={c.key} style={{ background:'rgba(255,255,255,0.07)', border:'1px solid rgba(255,255,255,0.13)', borderRadius:10, padding:'10px 18px', textAlign:'center', minWidth:90 }}>
                  <div style={{ fontFamily:"'JetBrains Mono',monospace", fontSize:10, color:'rgba(255,255,255,0.45)', marginBottom:4, letterSpacing:'.08em' }}>{c.abbr}</div>
                  <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:26, fontWeight:700, color:'#fff', lineHeight:1 }}>{results[c.key]?.score ?? results[c.key]}</div>
                  <div style={{ fontSize:10, color:'rgba(255,255,255,0.35)', marginTop:3 }}>{c.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* ── Examiner feedback ── */}
          {results.overall_feedback && (
            <div style={{ background:'#fff', border:'1px solid #e8edf5', borderRadius:12, padding:'14px 18px', marginBottom:16, boxShadow:'0 1px 4px rgba(26,42,74,0.06)' }}>
              <div style={{ fontFamily:"'JetBrains Mono',monospace", fontSize:10, letterSpacing:'.15em', color:'#b8860b', marginBottom:6, textTransform:'uppercase' }}>Examiner Feedback</div>
              <div style={{ fontSize:13.5, color:'#1a2233', lineHeight:1.8 }}>{results.overall_feedback}</div>
            </div>
          )}

          {/* ── Criterion breakdown ── */}
          {CRITERIA.some(c => results[c.key]?.why_score) && (
            <div style={{ background:'#fff', border:'1px solid #e8edf5', borderRadius:12, padding:'18px 18px 6px', marginBottom:16, boxShadow:'0 1px 4px rgba(26,42,74,0.06)' }}>
              <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:17, fontWeight:700, color:'#1a2a4a', marginBottom:14 }}>Criterion Breakdown</div>
              {CRITERIA.map(c => (
                <CriterionCard key={c.key} criterionKey={c.abbr} criterion={results[c.key]} label={c.label}/>
              ))}
            </div>
          )}

          {/* ── Repeated vocabulary ── */}
          {Array.isArray(results.repeated_vocabulary) && results.repeated_vocabulary.length > 0 && (
            <div style={{ background:'#fff', border:'1px solid #e8edf5', borderRadius:12, marginBottom:16, overflow:'hidden', boxShadow:'0 1px 4px rgba(26,42,74,0.06)' }}>
              <RepeatedVocabulary vocab={results.repeated_vocabulary}/>
            </div>
          )}

          {/* ── Legend ── */}
          <div style={{ display:'flex', gap:16, flexWrap:'wrap', marginBottom:14, padding:'10px 14px', background:'#fff', border:'1px solid #e8edf5', borderRadius:10, boxShadow:'0 1px 4px rgba(26,42,74,0.04)' }}>
            <div style={{ fontSize:11.5, color:'#5a6a88', fontWeight:600 }}>Tap a highlighted word to see the explanation:</div>
            <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
              <span style={{ fontSize:11, color:'#c0392b', background:'rgba(192,57,43,0.08)', borderBottom:'2px solid #c0392b', padding:'1px 7px', borderRadius:3 }}>error in your essay</span>
              <span style={{ fontSize:11, color:'#1a7a4a', background:'rgba(26,122,74,0.08)', borderBottom:'2px solid #1a7a4a', padding:'1px 7px', borderRadius:3 }}>improvement made</span>
            </div>
          </div>

          {/* ── Mobile toggle ── */}
          <div className="tm-toggle">
            <button onClick={()=>setMobileView('original')}
              style={{ flex:1, padding:'10px 0', background:mobileView==='original'?'#1a2a4a':'#fff', color:mobileView==='original'?'#fff':'#5a6a88', border:'none', cursor:'pointer', fontSize:13, fontWeight:mobileView==='original'?600:400 }}>
              Your Essay
            </button>
            <button onClick={()=>setMobileView('improved')}
              style={{ flex:1, padding:'10px 0', background:mobileView==='improved'?'#1a7a4a':'#fff', color:mobileView==='improved'?'#fff':'#5a6a88', border:'none', cursor:'pointer', fontSize:13, fontWeight:mobileView==='improved'?600:400 }}>
              Improved Version
            </button>
          </div>

          {/* ── Two-column comparison ── */}
          <div className="tm-grid">

            {/* LEFT — Your Essay */}
            <div className={mobileView === 'improved' ? 'tm-mobile-hide' : ''}
              style={{ background:'#fff', border:'1px solid #e8edf5', borderRadius:12, overflow:'hidden', boxShadow:'0 2px 8px rgba(26,42,74,0.07)' }}>
              <div style={{ background:'#fff5f5', borderBottom:'1px solid #fecdd3', padding:'12px 16px', display:'flex', alignItems:'center', gap:8 }}>
                <div style={{ width:10, height:10, borderRadius:'50%', background:'#c0392b' }}/>
                <div style={{ fontSize:13, fontWeight:700, color:'#c0392b' }}>Your Essay</div>
                <div style={{ marginLeft:'auto', fontFamily:"'JetBrains Mono',monospace", fontSize:10, color:'#9aa3b8' }}>{wc} words</div>
              </div>
              <div style={{ padding:'18px 20px', fontSize:14, lineHeight:2.1, fontFamily:"'Times New Roman',serif", color:'#1a1a1a' }}>
                {orig.length > 0 ? orig.map((seg, i) => {
                  const key = `orig-${i}`;
                  const isActive = activeTooltip === key;
                  const c = ERROR_COLORS[seg.errorType] || ERROR_COLORS.grammar;
                  if (!seg.hasError) return <span key={i}>{seg.text}</span>;
                  return (
                    <span key={i} style={{ position:'relative', display:'inline' }}>
                      <span onClick={e => { e.stopPropagation(); setActiveTooltip(isActive ? null : key); }}
                        style={{ background:c.bg, borderBottom:`2px solid ${c.border}`, color:c.fg, cursor:'pointer', borderRadius:2, padding:'0 1px' }}>
                        {seg.text}
                      </span>
                      {isActive && <Tooltip errorType={seg.errorType} text={seg.explanation}/>}
                    </span>
                  );
                }) : <span style={{ color:'#9aa3b8', fontSize:13 }}>(no annotation available)</span>}
              </div>
            </div>

            {/* RIGHT — Improved Version */}
            <div className={mobileView === 'original' ? 'tm-mobile-hide' : ''}
              style={{ background:'#fff', border:'1px solid #e8edf5', borderRadius:12, overflow:'hidden', boxShadow:'0 2px 8px rgba(26,42,74,0.07)' }}>
              <div style={{ background:'#f0fdf4', borderBottom:'1px solid #bbf7d0', padding:'12px 16px', display:'flex', alignItems:'center', gap:8 }}>
                <div style={{ width:10, height:10, borderRadius:'50%', background:'#1a7a4a' }}/>
                <div style={{ fontSize:13, fontWeight:700, color:'#1a7a4a' }}>Improved Version</div>
              </div>
              <div style={{ padding:'18px 20px', fontSize:14, lineHeight:2.1, fontFamily:"'Times New Roman',serif", color:'#1a1a1a' }}>
                {impr.length > 0 ? impr.map((seg, i) => {
                  const key = `impr-${i}`;
                  const isActive = activeTooltip === key;
                  if (!seg.isCorrected) return <span key={i}>{seg.text}</span>;
                  return (
                    <span key={i} style={{ position:'relative', display:'inline' }}>
                      <span onClick={e => { e.stopPropagation(); setActiveTooltip(isActive ? null : key); }}
                        style={{ background:'rgba(26,122,74,0.10)', borderBottom:'2px solid #1a7a4a', color:'#1a7a4a', cursor:'pointer', borderRadius:2, padding:'0 1px' }}>
                        {seg.text}
                      </span>
                      {isActive && <Tooltip text={seg.explanation}/>}
                    </span>
                  );
                }) : <span style={{ color:'#9aa3b8', fontSize:13 }}>(no annotation available)</span>}
              </div>
            </div>

          </div>

          <button onClick={onBack}
            style={{ width:'100%', marginTop:20, background:'#1a2a4a', color:'#ffffff', border:'none', padding:13, borderRadius:9, fontSize:14, fontWeight:600, cursor:'pointer', fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
            ← Back
          </button>
        </div>
        <Footer branding={branding}/>
      </div>
    );
  }

  // ── WRITING ────────────────────────────────────────────────────
  return (
    <div style={{ minHeight:'100vh', background:'#f0f4f8', display:'flex', flexDirection:'column' }}>
      <div style={{ background:'#1a2a4a', padding:'10px 20px', display:'flex', alignItems:'center', justifyContent:'space-between', position:'sticky', top:0, zIndex:200 }}>
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:16, fontWeight:700, color:'#ffffff' }}>IELTS<span style={{color:'#b8860b'}}>COMPASS</span></div>
          <span style={{ fontFamily:"'JetBrains Mono',monospace", fontSize:10, background:'rgba(184,134,11,0.2)', color:'#b8860b', padding:'2px 9px', borderRadius:100, letterSpacing:'.1em' }}>TEST MODE</span>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:14 }}>
          <div style={{ background:timeBg, color:timeColor, fontFamily:"'JetBrains Mono',monospace", fontSize:18, fontWeight:700, padding:'5px 14px', borderRadius:8, letterSpacing:'0.05em' }}>
            ⏱ {formatTime(timeLeft)}
          </div>
          <div style={{ fontFamily:"'JetBrains Mono',monospace", fontSize:13, fontWeight:600, color:wc>=250?'#7adb98':'rgba(255,255,255,0.75)', background:'rgba(0,0,0,0.2)', padding:'4px 13px', borderRadius:6 }}>
            {wc} words {wc>=250?'✓':'/ 250 min'}
          </div>
        </div>
      </div>

      <div style={{ background:'#ffffff', borderBottom:'1px solid #dde3ed', padding:'12px 22px' }}>
        <div style={{ maxWidth:820, margin:'0 auto' }}>
          <MonoLabel style={{ marginBottom:4 }}>Question</MonoLabel>
          <div style={{ fontSize:13.5, color:'#1a2233', lineHeight:1.7, fontStyle:'italic' }}>{prompt.text}</div>
          {isTrialUser && (
            <div style={{ marginTop:8, background:'#fff8f0', border:'1px solid #f0d890', borderRadius:6, padding:'7px 11px', fontSize:12, color:'#7a4a00', fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
              📝 Trial question. <a href="https://wa.me/201008182425" target="_blank" rel="noreferrer" style={{ color:'#b8860b', fontWeight:600 }}>Upgrade for 50+ real IELTS exam questions →</a>
            </div>
          )}
        </div>
      </div>

      <div style={{ maxWidth:820, margin:'0 auto', padding:'20px', flex:1, width:'100%' }}>
        {timeLeft < 300 && (
          <div style={{ background:'#fff5f5', border:'1px solid rgba(192,57,43,0.3)', borderRadius:8, padding:'10px 14px', marginBottom:14, fontSize:13, color:'#c0392b', fontWeight:500 }}>
            ⚠ Less than 5 minutes remaining — wrap up your conclusion now.
          </div>
        )}
        {error && <div style={{ background:'#fff5f5', border:'1px solid rgba(192,57,43,0.3)', borderRadius:8, padding:'10px 14px', marginBottom:14, fontSize:13, color:'#c0392b' }}>{error}</div>}
        <textarea
          value={essay}
          onChange={e => setEssay(e.target.value)}
          placeholder="Begin writing your essay here. You have 40 minutes. Aim for at least 250 words. Write your introduction, two body paragraphs, and conclusion."
          spellCheck={false}
          autoCorrect="off"
          autoCapitalize="off"
          autoComplete="off"
          data-gramm="false"
          data-gramm_editor="false"
          data-enable-grammarly="false"
          style={{ width:'100%', minHeight:'calc(100vh - 340px)', background:'#ffffff', border:'1.5px solid #c0c0c0', borderRadius:6, padding:'18px 20px', color:'#1a1a1a', fontSize:14.5, lineHeight:2, fontFamily:"'Times New Roman', Times, serif", outline:'none', resize:'none', boxShadow:'0 1px 4px rgba(0,0,0,0.08)' }}
        />
        <div style={{ background:'#1a2a4a', borderRadius:'0 0 6px 6px', padding:'8px 16px', display:'flex', justifyContent:'space-between', alignItems:'center', marginTop:-2 }}>
          <div style={{ fontFamily:"'JetBrains Mono',monospace", fontSize:12, color:'rgba(255,255,255,0.65)' }}>
            📝 Word count: <strong style={{ color:wc>=250?'#7adb98':wc>=200?'#ffcc66':'rgba(255,255,255,0.9)', fontSize:14 }}>{wc}</strong>
            <span style={{ marginLeft:10, color:'rgba(255,255,255,0.4)' }}>
              {wc>=250 ? '✓ Minimum reached' : `${250-wc} more words needed`}
            </span>
          </div>
          <div style={{ width:160, height:6, background:'rgba(255,255,255,0.15)', borderRadius:100, overflow:'hidden' }}>
            <div style={{ width:`${Math.min(wc/250*100,100)}%`, height:'100%', background:wc>=250?'#7adb98':wc>=200?'#ffcc66':'#5090d8', borderRadius:100, transition:'width .3s' }}/>
          </div>
        </div>
        <div style={{ display:'flex', justifyContent:'flex-end', alignItems:'center', marginTop:12 }}>
          <button onClick={handleSubmit} disabled={loading}
            style={{ background:'#b8860b', color:'#ffffff', border:'none', padding:'11px 28px', borderRadius:7, fontSize:14, fontWeight:600, cursor:'pointer', fontFamily:"'Plus Jakarta Sans',sans-serif", boxShadow:'0 2px 8px rgba(184,134,11,0.3)', opacity:loading?0.6:1 }}>
            Submit Essay →
          </button>
        </div>
      </div>
    </div>
  );
}
